import { appConfig } from '../config/app.config'

export interface NavigationItem {
  enabled: boolean
  text: string
  path?: string
  icon: string
  children?: Record<string, NavigationItem>
}

function hasChildren(
  item: NavigationItem
): item is NavigationItem & { children: Record<string, NavigationItem> } {
  return item.children !== undefined && Object.keys(item.children).length > 0
}

export interface NavigationConfig {
  items: Record<string, NavigationItem>
}

export function getEnabledNavigationItems(): Record<string, NavigationItem> {
  const enabledItems: Record<string, NavigationItem> = {}

  Object.entries(appConfig.navigation.items).forEach(([key, item]) => {
    let shouldEnable = item.enabled

    if (key === 'tokenInspector') {
      shouldEnable = item.enabled && appConfig.features.showTokenInspector
    }

    if (shouldEnable) {
      if (hasChildren(item)) {
        const enabledChildren: Record<string, NavigationItem> = {}
        Object.entries(item.children).forEach(([childKey, childItem]) => {
          if (childItem.enabled) {
            enabledChildren[childKey] = childItem
          }
        })

        if (Object.keys(enabledChildren).length > 0) {
          enabledItems[key] = {
            ...item,
            children: enabledChildren
          }
        }
      } else {
        enabledItems[key] = item
      }
    }
  })

  return enabledItems
}

export function isNavigationItemEnabled(itemKey: string): boolean {
  const item = appConfig.navigation.items[itemKey as keyof typeof appConfig.navigation.items]
  if (!item) return false

  if (itemKey === 'tokenInspector') {
    return item.enabled && appConfig.features.showTokenInspector
  }

  return item.enabled
}

export function getNavigationItem(itemKey: string): NavigationItem | null {
  const items = getEnabledNavigationItems()
  return items[itemKey] || null
}

export function isRouteEnabled(routePath: string): boolean {
  const enabledPaths = new Set<string>()

  const normalize = (p: string) => {
    if (!p) return p
    return p.replace(/\/+$/, '') || '/'
  }

  const addPathsFromItems = (items: Record<string, NavigationItem>) => {
    Object.values(items).forEach((item) => {
      if (item.path) {
        enabledPaths.add(normalize(item.path))
      }

      if (hasChildren(item)) {
        Object.values(item.children).forEach((child) => {
          if (child.path) {
            enabledPaths.add(normalize(child.path))
          }
        })
      }
    })
  }

  const enabledItems = getEnabledNavigationItems()
  addPathsFromItems(enabledItems)

  const alwaysAllowedRoutes = ['/', '/login', '/signup', '/invite/register', '/dashboard']
  if (alwaysAllowedRoutes.includes(normalize(routePath))) {
    return true
  }

  return enabledPaths.has(normalize(routePath))
}
